import { logToSentry } from 'core/utils/sentry';

type ApiErrorAction = 'redirect-not-found' | 'redirect-login' | 'set-error-state';

type ApiErrorState = {
  status: number | null;
  message: string;
  canRetry: boolean;
};

type ApiErrorResult = {
  action: ApiErrorAction;
  errorState: ApiErrorState;
};

type HandleApiErrorOptions = {
  context: string;
  response: Record<string, unknown> | null;
  extra?: Record<string, unknown>;
};

export function handleApiError({ context, response, extra }: HandleApiErrorOptions): ApiErrorResult {
  const errorResponse = response?.error as { status?: number; data?: unknown } | undefined;
  const status = errorResponse?.status ?? null;

  logToSentry('error', `API error in ${context}`, {
    tags: {
      context,
      errorType: status ? `http_${status}` : 'network_error',
    },
    extra: {
      status,
      responseData: errorResponse?.data,
      currentPath: typeof globalThis.window === 'undefined' ? '' : globalThis.window.location.pathname,
      timestamp: new Date().toISOString(),
      ...extra,
    },
  });

  if (status === 404) {
    return {
      action: 'redirect-not-found',
      errorState: { status: 404, message: 'Resource not found', canRetry: false },
    };
  }

  if (status === 401) {
    return {
      action: 'redirect-login',
      errorState: { status: 401, message: 'Session expired', canRetry: false },
    };
  }

  if (status === 403) {
    return {
      action: 'set-error-state',
      errorState: { status: 403, message: 'Access denied', canRetry: false },
    };
  }

  if (status && status >= 500) {
    return {
      action: 'set-error-state',
      errorState: { status, message: 'Server error', canRetry: true },
    };
  }

  return {
    action: 'set-error-state',
    errorState: {
      status,
      message: status === null ? 'Network error' : `Unexpected error (${status})`,
      canRetry: true,
    },
  };
}

export type { ApiErrorAction, ApiErrorState, ApiErrorResult, HandleApiErrorOptions };
